import { SITE } from '@cardengine/shared';
import { useEffect, useState, type ReactNode } from 'react';
import { GameLog } from '../components/GameLog.js';
import { linkProps } from '../router.js';
import { useStore } from '../store.js';

/** Let the last card land before the result goes up. */
const LANDING_MS = 650;

export type SoloPhase = 'playing' | 'won' | 'stuck';

export interface SoloRoomProps {
  /** The game's name, for the header and the tab. */
  name: string;
  phase: SoloPhase;
  moveNumber: number;
  /** The one-line readout beside the name: stock left, cells free, and so on. */
  status: string;
  board: ReactNode;
  /** How this particular game phrases a dead deal. */
  stuckTitle: string;
  stuckBody: string;
}

/**
 * The table a patience game is played at.
 *
 * Deliberately not a branch inside `GameRoom`: that view is built around seats,
 * turns and a chat panel, and none of the three exist here. Sharing the header
 * markup keeps them looking like one site without pretending they are one
 * screen.
 *
 * Shared between Solitaire and FreeCell, which is what building the second one
 * settled. Everything on this screen except the board itself and a handful of
 * sentences is about a seat rather than a game: the moves readout, the new-deal
 * confirmation, the log panel, the result modal and the wins tally are the same
 * screen twice over. The boards genuinely differ — one has a stock and hidden
 * cards, the other has cells and nothing hidden — so they stay apart, and are
 * passed in.
 */
export function SoloRoom({ name, phase, moveNumber, status, board, stuckTitle, stuckBody }: SoloRoomProps) {
  const lobby = useStore((s) => s.lobby);
  const leaveLobby = useStore((s) => s.leaveLobby);
  const playAgain = useStore((s) => s.playAgain);
  const setLogPanelOpen = useStore((s) => s.setLogPanelOpen);

  const [confirming, setConfirming] = useState(false);
  const [panelOpen, setPanelOpen] = useState(
    () => typeof window !== 'undefined' && window.innerWidth >= 900,
  );

  useEffect(() => {
    setLogPanelOpen(panelOpen);
    return () => setLogPanelOpen(false);
  }, [panelOpen, setLogPanelOpen]);

  const over = phase === 'won' || phase === 'stuck';
  const [showResult, setShowResult] = useState(false);

  useEffect(() => {
    if (!over) {
      setShowResult(false);
      return;
    }
    const timer = setTimeout(() => setShowResult(true), LANDING_MS);
    return () => clearTimeout(timer);
  }, [over]);

  if (!lobby) {
    return (
      <main className="game">
        <p className="game__loading">Dealing…</p>
      </main>
    );
  }

  return (
    <main className="game">
      <header className="game__header game__header--solo">
        <a className="game__brand" {...linkProps('/')} title={`${SITE.name} home`}>
          <span className="game__brand-mark" aria-hidden="true">
            ♠
          </span>
          <span className="game__brand-name">{SITE.name}</span>
        </a>
        <span className="game__code">{name}</span>
        <span className="game__turn">
          {moveNumber} moves · {status}
        </span>
        <div className="game__header-actions">
          <button
            type="button"
            className="btn btn--secondary btn--small"
            // Only a deal actually in progress is worth protecting; a finished
            // or untouched one can just be redealt.
            onClick={() => (phase === 'playing' && moveNumber > 0 ? setConfirming(true) : playAgain())}
          >
            New deal
          </button>
          <button
            type="button"
            className={`btn btn--ghost btn--small ${panelOpen ? 'is-active' : ''}`}
            onClick={() => setPanelOpen((open) => !open)}
            aria-expanded={panelOpen}
          >
            {panelOpen ? 'Hide log' : 'Show log'}
          </button>
          <button type="button" className="btn btn--ghost btn--small" onClick={() => void leaveLobby()}>
            Leave
          </button>
        </div>
      </header>

      <div className={`game__body ${panelOpen ? 'game__body--with-chat' : ''}`}>
        {board}
        {panelOpen && (
          <aside className="game__chat">
            <div className="panel">
              <div className="panel__tabs">
                <span className="panel__tab is-active">Game log</span>
              </div>
              <div className="panel__body">
                <GameLog />
              </div>
            </div>
          </aside>
        )}
      </div>

      {confirming && (
        <ConfirmNewDeal
          moves={moveNumber}
          onCancel={() => setConfirming(false)}
          onConfirm={() => {
            setConfirming(false);
            playAgain();
          }}
        />
      )}

      {showResult && (
        <Result phase={phase} moveNumber={moveNumber} stuckTitle={stuckTitle} stuckBody={stuckBody} />
      )}
    </main>
  );
}

/**
 * Abandoning a deal in progress cannot be undone, so a deal with moves on it
 * asks first. A deal you have not touched yet has nothing to lose, and making
 * someone confirm that would just be a click in the way.
 */
function ConfirmNewDeal({
  moves,
  onCancel,
  onConfirm,
}: {
  moves: number;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div className="modal" role="dialog" aria-modal="true" aria-label="Start a new deal">
      <div className="modal__panel">
        <header className="modal__head">
          <h2 className="modal__title">Start a new deal?</h2>
        </header>
        <p className="modal__hint">
          This one will be abandoned — {moves} {moves === 1 ? 'move' : 'moves'} in. It does not count as
          a loss.
        </p>
        <div className="modal__actions">
          <button type="button" className="btn btn--ghost" onClick={onCancel}>
            Keep playing
          </button>
          <button type="button" className="btn btn--primary" onClick={onConfirm} autoFocus>
            New deal
          </button>
        </div>
      </div>
    </div>
  );
}

function Result({
  phase,
  moveNumber,
  stuckTitle,
  stuckBody,
}: {
  phase: SoloPhase;
  moveNumber: number;
  stuckTitle: string;
  stuckBody: string;
}) {
  const lobby = useStore((s) => s.lobby);
  const playAgain = useStore((s) => s.playAgain);
  const rematch = useStore((s) => s.rematch);
  const leaveLobby = useStore((s) => s.leaveLobby);

  if (phase !== 'won' && phase !== 'stuck') return null;

  const won = phase === 'won';
  const wins = lobby?.members[0]?.wins ?? 0;

  return (
    <div className="modal" role="dialog" aria-modal="true" aria-label="Result">
      <div className="modal__panel">
        <header className="modal__head">
          <h2 className="modal__title">{won ? 'Solved! 🎉' : stuckTitle}</h2>
          <span className="modal__round">Deal {lobby?.roundsPlayed ?? 1}</span>
        </header>

        <p className="modal__hint">{won ? `Every card home in ${moveNumber} moves.` : stuckBody}</p>
        {wins > 0 && (
          <p className="modal__hint">
            {wins} {wins === 1 ? 'deal' : 'deals'} solved in this sitting.
          </p>
        )}

        <div className="modal__actions">
          <button type="button" className="btn btn--ghost" onClick={() => void leaveLobby()}>
            Leave
          </button>
          <div className="modal__actions-group">
            <button type="button" className="btn btn--secondary" onClick={rematch}>
              Change settings
            </button>
            <button type="button" className="btn btn--primary" onClick={playAgain} autoFocus>
              Deal again
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
